import { ReactNode, useMemo } from 'react';
import { paramCase } from 'change-case';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';

import { LinearProgress } from '@app/ui/progress';
import { useIsMobile } from '@app/app/hooks/useIsMobile';
import { calculateSurveyProgress, isVisible } from '@app/dynamic/utils';

import { getProgressStatusConfig } from '@app/shared/utils';
import { FinishAndSubmit } from '@app/shared/components/buttons/FinishAndSubmit';
import { isEditableStatus } from '@app/tenant';
import { applicationConfig } from '../../configs';
import { Application } from '../../types/Application';
import { SurveyItem } from './SurveyItem';

export interface ApplicationListProps {
  readonly item: Application;
  readonly onClick: (survey: string) => void;
  readonly onSubmit?: () => void;
  readonly isAdmin?: boolean;
  /** Admin view only — rendered right-aligned on the DC's single header row (see below). */
  readonly actions?: ReactNode;
}

export const ApplicationList = ({ onClick, item, isAdmin, onSubmit, actions }: ApplicationListProps) => {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile();

  const applicationProgress = useMemo(() => calculateSurveyProgress(applicationConfig.surveys, item), [item]);

  const surveysConfig = useMemo(() => applicationConfig.surveys.filter(survey => isVisible(survey, item)), [item]);

  // SAA-82/S9 — "N of M sections completed": a section is complete under the SAME predicate
  // `getProgressStatusConfig` already uses for each row's own chip (`populatedPageCount ===
  // pageCount`), so this count can never disagree with the rows rendered below it. M is
  // `surveysConfig.length` (the `isVisible`-filtered list), never a hardcoded 12.
  const completedSectionsCount = useMemo(
    () =>
      surveysConfig.filter(survey => {
        const progressConfig = applicationProgress.populatedSurveyCountMap[survey.name];

        return progressConfig?.populatedPageCount === progressConfig?.pageCount;
      }).length,
    [surveysConfig, applicationProgress],
  );

  const notFinished = applicationProgress.progressTotal !== 100;

  const isSubmittedSurrogate = !isEditableStatus(item.status) && !item.isUnlocked && !isAdmin;

  return (
    <>
      {/* Shared component: the surrogate's own page (`UserApplicationItem`) passes no `isAdmin` and
      keeps the linear bar. Hooks above stay unconditional — only the render branches. */}
      {isAdmin ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
            flexWrap: 'wrap',
            mb: 3,
          }}
        >
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            {/* Two rings: determinate CircularProgress draws only the arc, so the track is a
            separate value={100} layer underneath. */}
            <CircularProgress
              variant="determinate"
              value={100}
              size={72}
              thickness={4}
              sx={{ color: 'progressTrack.main' }}
            />
            <CircularProgress
              variant="determinate"
              value={applicationProgress.progressTotal}
              size={72}
              thickness={4}
              sx={{ position: 'absolute', left: 0, color: 'progressAccent.main' }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* DC: 18px / 700 in the ring's own accent colour, not default body text. */}
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'progressAccent.main' }}>
                {applicationProgress.progressTotal}%
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography component="h3" sx={{ fontSize: 24, fontWeight: 600, color: 'text.primary' }}>
              {t('user.page.single.subtitle')}
            </Typography>
            <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: '2px' }}>
              {t('user.page.single.sectionsCompleted', {
                done: completedSectionsCount,
                total: surveysConfig.length,
              })}
            </Typography>
          </Box>
          {actions && (
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>{actions}</Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: {
              sm: 'flex',
            },
            alignItems: 'center',
            mb: { xs: 3, md: 4 },
          }}
        >
          <Typography
            variant={isMobile ? 'h2' : 'h3'}
            component="h3"
            sx={{
              whiteSpace: 'nowrap',
              mr: {
                sm: 3,
                md: 5,
              },
              mb: {
                xs: 1,
                sm: 0,
              },
            }}
          >
            {t('user.page.single.currentProgress')}
          </Typography>
          <LinearProgress value={applicationProgress.progressTotal} />
        </Box>
      )}
      <Paper
        sx={{
          borderRadius: '20px',
        }}
        variant="outlined"
        elevation={0}
      >
        {surveysConfig.map(survey => {
          const progressConfig = applicationProgress.populatedSurveyCountMap[survey.name];

          const { status, icon, iconButton, color } = getProgressStatusConfig(
            progressConfig.populatedPageCount,
            progressConfig.pageCount,
          );
          const handleSurvey = () => onClick(paramCase(survey.name));

          return (
            <SurveyItem
              key={survey.name}
              surveyLabel={t(`application.field.survey.option.${survey.name}.name`)}
              progressConfig={progressConfig}
              statusLabel={t(`application.field.form.status.${status}`)}
              color={color}
              icon={icon}
              iconButton={iconButton}
              isAdmin={isAdmin || false}
              isSubmitted={isSubmittedSurrogate}
              isMobile={isMobile}
              onClick={handleSurvey}
            />
          );
        })}
      </Paper>

      {!isAdmin && (isEditableStatus(item.status) || item.isUnlocked) && (
        <FinishAndSubmit
          onSubmit={onSubmit}
          notFinished={notFinished}
          tooltipText={t('application.button.finishAndSubmit.description')}
          buttonText={t('application.button.finishAndSubmit.name')}
        />
      )}
    </>
  );
};
